/**
 * Compact storage of a real-world theatre map: the committed JSON (`data/<id>.json`) written by
 * `scripts/build-theatres.ts` and decoded at run time by `loadTheatreMap`. Per-hex arrays travel
 * as base64 byte strings; rivers as corner-lattice vertex chains (mapgen's lattice: vertex 2h =
 * top corner of hex h, 2h + 1 = its bottom corner), so their world points are exact on every
 * JavaScript engine. Pure and dependency-free (no atob/Buffer: the engine also runs in workers).
 */
import { CAPITAL_VP_BONUS, CITY_YIELDS } from '../core/constants';
import { HEX_SIZE, HexGrid } from '../core/hex';
import { type City, type GameMap, isWater, type RiverPath } from '../core/types';

export const THEATRE_DATA_VERSION = 1;

/** A city row: name, hex, size, capital-of (255 = none), vp, industry, manpower. */
export type CityRow = [string, number, 1 | 2 | 3, number, number, number, number];

export interface TheatreData {
  v: number;
  id: string;
  width: number;
  height: number;
  /** Kilometres between neighbouring hex centres. */
  hexKm: number;
  /** Nation count (factions); owner values 0..n-1, 255 = water / neutral. */
  nations: number;
  terrain: string;
  elevation: string;
  moisture: string;
  riverEdges: string;
  roads: string;
  rails: string;
  owner: string;
  /** Region per hex + 1 as little-endian uint16 (0 = water). */
  region: string;
  /** Rivers, source → mouth: lattice vertices and a width class per vertex. */
  rivers: { v: number[]; w: number[] }[];
  cities: CityRow[];
  /** Region name and label-anchor hex, by region id. */
  regions: [string, number][];
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_INDEX = (() => {
  const t = new Int16Array(128).fill(-1);
  for (let i = 0; i < B64.length; i++) t[B64.charCodeAt(i)] = i;
  return t;
})();

export function encodeBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i];
    const b = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const c = i + 2 < bytes.length ? bytes[i + 2] : 0;
    out += B64[a >> 2] + B64[((a & 3) << 4) | (b >> 4)];
    out += i + 1 < bytes.length ? B64[((b & 15) << 2) | (c >> 6)] : '=';
    out += i + 2 < bytes.length ? B64[c & 63] : '=';
  }
  return out;
}

export function decodeBase64(text: string): Uint8Array {
  let pad = 0;
  while (pad < 2 && text.charCodeAt(text.length - 1 - pad) === 61) pad++;
  const out = new Uint8Array((text.length / 4) * 3 - pad);
  let o = 0;
  for (let i = 0; i < text.length; i += 4) {
    const n =
      (B64_INDEX[text.charCodeAt(i)] << 18) |
      (B64_INDEX[text.charCodeAt(i + 1)] << 12) |
      (Math.max(0, B64_INDEX[text.charCodeAt(i + 2)]) << 6) |
      Math.max(0, B64_INDEX[text.charCodeAt(i + 3)]);
    if (o < out.length) out[o++] = (n >> 16) & 255;
    if (o < out.length) out[o++] = (n >> 8) & 255;
    if (o < out.length) out[o++] = n & 255;
  }
  return out;
}

/** World position of a lattice vertex (exact: centre ± HEX_SIZE, no trigonometry). */
function vertexXY(grid: HexGrid, v: number): [number, number] {
  const h = v >> 1;
  return [grid.cx(h), grid.cy(h) + (v & 1 ? HEX_SIZE : -HEX_SIZE)];
}

/** Default yields of a city size, plus the capital bonus. */
export function defaultYields(
  size: 1 | 2 | 3,
  capital: boolean,
): { vp: number; industry: number; manpower: number } {
  const y = CITY_YIELDS[size];
  return { vp: y.vp + (capital ? CAPITAL_VP_BONUS : 0), industry: y.industry, manpower: y.manpower };
}

/** Decode the committed data into a fresh GameMap (typed arrays are new on every call). */
export function decodeTheatre(data: TheatreData, name: string, seed: number): GameMap {
  if (data.v !== THEATRE_DATA_VERSION) throw new Error(`theatre ${data.id}: data version ${data.v}`);
  const { width, height } = data;
  const grid = new HexGrid(width, height);
  const n = grid.count;
  const bytes = (field: string): Uint8Array => {
    const b = decodeBase64(field);
    if (b.length !== n) throw new Error(`theatre ${data.id}: array of ${b.length} bytes, expected ${n}`);
    return b;
  };
  const terrain = bytes(data.terrain);
  const regionRaw = decodeBase64(data.region);
  const region = new Int16Array(n);
  for (let h = 0; h < n; h++) region[h] = (regionRaw[2 * h] | (regionRaw[2 * h + 1] << 8)) - 1;
  const cityAt = new Int16Array(n).fill(-1);
  const cities: City[] = data.cities.map(([cityName, hex, size, capitalOf, vp, industry, manpower], id) => {
    cityAt[hex] = id;
    let coastal = false;
    grid.forNeighbours(hex, (m) => {
      if (isWater(terrain[m])) coastal = true;
    });
    return { id, hex, name: cityName, size, capitalOf, vp, industry, manpower, coastal };
  });
  const rivers: RiverPath[] = data.rivers.map((r) => {
    const points: number[] = [];
    for (const v of r.v) points.push(...vertexXY(grid, v));
    return { points, widths: r.w.slice() };
  });
  return {
    seed,
    name,
    width,
    height,
    terrain,
    elevation: bytes(data.elevation),
    moisture: bytes(data.moisture),
    riverEdges: bytes(data.riverEdges),
    rivers,
    roads: bytes(data.roads),
    rails: bytes(data.rails),
    cities,
    cityAt,
    initialOwner: bytes(data.owner),
    region,
    regions: data.regions.map(([regionName, hex], id) => ({ id, name: regionName, hex })),
    factionCount: data.nations,
    hexKm: data.hexKm,
  };
}

/** The inverse, for the build script: a GameMap (and its river vertex chains) as TheatreData. */
export function encodeTheatre(id: string, map: GameMap, riverVertices: readonly number[][]): TheatreData {
  const n = map.width * map.height;
  const region = new Uint8Array(2 * n);
  for (let h = 0; h < n; h++) {
    const r = map.region[h] + 1;
    region[2 * h] = r & 255;
    region[2 * h + 1] = r >> 8;
  }
  return {
    v: THEATRE_DATA_VERSION,
    id,
    width: map.width,
    height: map.height,
    hexKm: map.hexKm ?? 10,
    nations: map.factionCount,
    terrain: encodeBase64(map.terrain),
    elevation: encodeBase64(map.elevation),
    moisture: encodeBase64(map.moisture),
    riverEdges: encodeBase64(map.riverEdges),
    roads: encodeBase64(map.roads),
    rails: encodeBase64(map.rails),
    owner: encodeBase64(map.initialOwner),
    region: encodeBase64(region),
    rivers: map.rivers.map((r, i) => ({ v: [...riverVertices[i]], w: [...r.widths] })),
    cities: map.cities.map((c) => [c.name, c.hex, c.size, c.capitalOf, c.vp, c.industry, c.manpower]),
    regions: map.regions.map((r) => [r.name, r.hex]),
  };
}
